import apiClient from '../utils/apiClient.js';
import { normalizeLanguage } from '../utils/languageUtils.js';
import i18n from '../i18n';

export class EddyBetaApiError extends Error {
  constructor(message, { code, status, retryable = false, sessionId = null } = {}) {
    super(message);
    this.name = 'EddyBetaApiError';
    this.code = code;
    this.status = status;
    this.retryable = retryable;
    this.sessionId = sessionId;
  }
}

async function responseJson(response) {
  return response.json().catch(() => ({}));
}

async function requireOk(response, fallbackMessage) {
  const data = await responseJson(response);
  if (!response.ok) {
    throw new EddyBetaApiError(data.error || fallbackMessage, {
      code: data.code,
      status: response.status,
      retryable: Boolean(data.retryable),
      sessionId: data.sessionId || null
    });
  }
  return data;
}

export async function sendBetaEddyMessage({
  text,
  sessionId = null,
  language = normalizeLanguage(i18n.language) || 'en',
  responseStyle = 'quick'
}) {
  const response = await apiClient.post('/api/chatbot/beta/generate', {
    text,
    language,
    responseStyle,
    ...(sessionId ? { sessionId } : {})
  }, {}, { maxRetries: 0 });

  const data = await requireOk(response, 'Beta chat service unavailable');
  if (!data?.content || !data?.sessionId) {
    throw new EddyBetaApiError('Invalid response from beta chat service', {
      code: 'INVALID_RESPONSE',
      status: response.status
    });
  }

  return data;
}

export async function listBetaEddySessions() {
  const response = await apiClient.get('/api/chatbot/beta/sessions');
  const data = await requireOk(response, 'Unable to load conversations');
  return Array.isArray(data.sessions) ? data.sessions : [];
}

export async function getBetaEddySession(sessionId) {
  const response = await apiClient.get(`/api/chatbot/beta/sessions/${encodeURIComponent(sessionId)}`);
  return requireOk(response, 'Unable to load the conversation');
}

export async function deleteBetaEddySession(sessionId) {
  const response = await apiClient.delete(`/api/chatbot/beta/sessions/${encodeURIComponent(sessionId)}`, {}, { maxRetries: 0 });
  return requireOk(response, 'Unable to delete the conversation');
}

const BAND_LIMITS = [3, 6, 10, 15, 20];

function bandForPosition(position) {
  const bandIndex = BAND_LIMITS.findIndex((maximum) => position <= maximum);
  if (bandIndex < 0) return String(position);
  const previousMaximum = bandIndex === 0 ? 0 : BAND_LIMITS[bandIndex - 1];
  return `${String.fromCharCode(65 + bandIndex)}${position - previousMaximum}`;
}

function normalizeRanking(rawRanking) {
  if (!rawRanking || typeof rawRanking !== 'object' || Array.isArray(rawRanking)) return [];
  const banded = Object.entries(rawRanking)
    .filter(([key, value]) => /^band[A-E]$/.test(key) && Array.isArray(value))
    .flatMap(([key, values]) => values.map((code, index) => ({
      band: `${key.at(-1)}${index + 1}`,
      code: String(code || '').trim().toUpperCase()
    })));
  if (banded.length > 0) return banded.filter(({ code }) => code);

  return Object.entries(rawRanking)
    .map(([code, index]) => ({ code: String(code || '').trim().toUpperCase(), index: Number(index) }))
    .filter(({ code, index }) => /^JS[A-Z0-9]{4}$/.test(code) && Number.isInteger(index) && index >= 0 && index < 20)
    .sort((left, right) => left.index - right.index)
    .map(({ code, index }) => ({ band: bandForPosition(index + 1), code }));
}

export function normalizeBetaEddyProfile(user) {
  const scores = user?.dse_scores && typeof user.dse_scores === 'object' && !Array.isArray(user.dse_scores)
    ? Object.entries(user.dse_scores)
      .filter(([, grade]) => grade !== null && grade !== undefined && String(grade).trim())
      .map(([subject, grade]) => ({ subject, grade: String(grade) }))
    : [];

  return {
    alias: [user?.Nickname, user?.First_Name, user?.User_ID]
      .find((value) => typeof value === 'string' && value.trim())?.trim() || 'Student',
    scores,
    jupasRanking: normalizeRanking(user?.jupas_programme_order)
  };
}

export async function loadBetaEddyProfile() {
  const response = await apiClient.get('/api/auth/me');
  const user = await requireOk(response, 'Unable to load student profile');
  return normalizeBetaEddyProfile(user);
}
